var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { v4 as uuid } from 'uuid';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, IconButton, TextField, Typography } from '@material-ui/core';
import { Add, ExpandMore, RemoveCircleOutlineOutlined } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { AppSnackbar, ConfigFieldControl, ConfigTextField, useDebounce, useFromLocalCache, useTimeout } from '@sierralabs/core-ui';
import { useQueryContext } from '@sierralabs/query-ui';
import { findTask, findTaskEntity, TaskBoardStatusAction, useWorkflowItem, WorkflowPicker } from '@sierralabs/workflow-ui';
import { parseTaskStatus } from '@sierralabs/workflow-core';
import { KanbanBoard } from '../../common/kanban-board/KanbanBoard';
import { useKanbanBoardContext } from '../../common/kanban-board/KanbanBoardContext';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import SiteQueryPicker from '../../common/pickers/SiteQueryPicker';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useGetSiteKanbanBoard } from '../../hooks/site-kanban-board/useGetSiteKanbanBoard';
import { useSiteKanbanBoard } from '../../hooks/site-kanban-board/useSiteKanbanBoard';
import { useSiteKanbanBoardColumns } from '../../hooks/site-kanban-board/useSiteKanbanBoardColumns';
import { useFindNearestRouteComponent } from '../../hooks/site-route/useFindNearestRouteComponent';
import { getRouteLocalCacheKey } from '../../hooks/site-route/useRouteLocalCacheKey';
import { useSiteTraceBoardParams } from '../../hooks/site-trace-board/useSiteTraceBoardParams';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
function getColumnsKeys(component) {
    var _a;
    const keys = {
        refetch: [],
        refresh: [] // refresh items state
    };
    for (const column of ((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || []) {
        const fields = column.itemFields || {};
        // trigger refetch when applying status filters to update board items
        const refetchKeys = [
            column.id,
            column.queryId,
            column.filterByStatus ? `${column.workflowPath}-${fields.status}` : false
        ].join(' ');
        keys.refetch.push(refetchKeys);
        // trigger refresh when fields changed to update card info
        const refreshKeys = [
            column.id,
            fields.title,
            fields.description,
            fields.status,
            fields.workflowId,
            fields.taskId,
            fields.firstName,
            fields.lastName,
            fields.deleted
        ].join(' ');
        keys.refresh.push(refreshKeys);
    }
    return { refetch: keys.refetch.join(' '), refresh: keys.refresh.join(' ') };
}
export const SiteKanbanBoard = props => {
    var _a;
    const history = useHistory();
    const didFetchRef = useRef(false);
    const columnsKeysRef = useRef({ refetch: '', refresh: '' });
    const [search] = useQueryParam('search', withDefault(StringParam, ''));
    const debouncedSearch = useDebounce(search, 300);
    const component = useSubscribeComponentChanges(SiteKanbanBoardPlugin, props);
    const { editing, isHover, hoverProps, subscribe, unsubscribe } = useSiteContext();
    const [error, setErrorState] = useState();
    const [debounceGet, clearDebouncedGet] = useTimeout();
    const { columns: boardColumns, items, setup, setItems, refresh } = useSiteKanbanBoard(component);
    const boardParams = useSiteTraceBoardParams(component);
    const { columns, getItemPath } = useSiteKanbanBoardColumns(component, boardColumns);
    const { isLoading, error: boardError, getBoard, setBoardLoading } = useGetSiteKanbanBoard();
    const localCache = useFromLocalCache();
    const route = useFindNearestRouteComponent(component);
    columnsKeysRef.current || (columnsKeysRef.current = getColumnsKeys(component));
    const classes = ['site-kanban-board'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const allQueryIds = (((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || [])
        .map(column => column.queryId)
        .filter((queryId) => !!queryId);
    const queryIds = useMemo(() => {
        return Array.from(allQueryIds);
        // re-evaluate only when component queryIds change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allQueryIds.join(',')]);
    const expressionMaps = useMemo(() => {
        const expressionMaps = {};
        for (const queryId of queryIds) {
            const key = getRouteLocalCacheKey(route, { queryId });
            const query = localCache.get(key);
            if (query === null || query === void 0 ? void 0 : query.config) {
                expressionMaps[queryId] = query.config;
            }
        }
        return expressionMaps;
    }, [localCache, queryIds, route]);
    // set errors only when viewing (avoid errors while still configuring)
    const setError = useCallback(value => !editing && setErrorState(value), [editing]);
    const findEventName = useCallback((status, target) => {
        var _a, _b;
        const taskStatus = parseTaskStatus(status);
        const targetStatus = parseTaskStatus(target);
        const column = columns.find(column => { var _a; return ((_a = column.state) === null || _a === void 0 ? void 0 : _a.name) === taskStatus; });
        const event = (_b = (_a = column === null || column === void 0 ? void 0 : column.state) === null || _a === void 0 ? void 0 : _a.events) === null || _b === void 0 ? void 0 : _b.find(event => {
            return event.transitions.some(transition => targetStatus === parseTaskStatus(transition.target));
        });
        return event === null || event === void 0 ? void 0 : event.name;
    }, [columns]);
    const getBoardWithFilters = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setBoardLoading(true);
        // since board size may be large, avoid storing it into
        // a state to save up memory since it is only used once
        const board = yield getBoard(component.id, {
            expressionMaps,
            params: boardParams,
            search: debouncedSearch
        });
        board && setup(board);
        setBoardLoading(false);
    }), [
        boardParams,
        component.id,
        debouncedSearch,
        expressionMaps,
        getBoard,
        setBoardLoading,
        setup
    ]);
    useEffect(() => {
        // trigger refetch when key changes
        const handle = (component) => {
            const keys = getColumnsKeys(component);
            if (columnsKeysRef.current.refetch !== keys.refetch) {
                // debounce to allow component to update first
                debounceGet(() => getBoardWithFilters(), 1000);
            }
            else if (editing && columnsKeysRef.current.refresh !== keys.refresh) {
                // in edit mode, refresh items when config is updated
                // refetching board, skip refresh
                refresh();
            }
            columnsKeysRef.current = keys;
        };
        subscribe(component, handle);
        return () => {
            clearDebouncedGet();
            unsubscribe(component, handle);
        };
    }, [
        component,
        editing,
        clearDebouncedGet,
        debounceGet,
        getBoardWithFilters,
        subscribe,
        refresh,
        unsubscribe
    ]);
    // during edit mode, debounce fetching to handle
    // cases where component is rendering multiple times
    useEffect(() => {
        // always no debounce for the first fetch
        if (!didFetchRef.current || !editing) {
            didFetchRef.current = true;
            getBoardWithFilters();
        }
        else {
            return debounceGet(() => getBoardWithFilters(), 200);
        }
    }, [editing, debounceGet, getBoardWithFilters]);
    useEffect(() => {
        setError(boardError);
    }, [boardError, setError]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteKanbanBoardPlugin }, props),
        React.createElement(KanbanBoard, { items: items, columns: columns, disabled: editing, isLoading: isLoading, className: classes.join(' '), containerProps: hoverProps(component), setItems: setItems, findEvent: findEventName, onSelectItem: item => {
                const path = getItemPath(item);
                path && history.push(path);
            } },
            React.createElement(SiteKanbanBoardAction, { onError: setError, onChange: () => getBoardWithFilters() })),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, (error === null || error === void 0 ? void 0 : error.message) || 'An error occurred.')));
};
function findTaskAndEntity(taskId) {
    const fetchTask = findTask(taskId);
    const fetchTaskEntity = findTaskEntity(taskId);
    const abort = () => {
        fetchTask.request.abort();
        fetchTaskEntity.request.abort();
    };
    const handle = () => __awaiter(this, void 0, void 0, function* () {
        const [taskResult, taskEntityResult] = yield Promise.all([
            fetchTask.result,
            fetchTaskEntity.result
        ]);
        return {
            task: taskResult.data,
            taskEntity: taskEntityResult.data,
            error: taskResult.error || taskEntityResult.error
        };
    });
    return { abort, result: handle() };
}
const SiteKanbanBoardAction = props => {
    const { onChange, onError } = props;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const [task, setTask] = useState();
    const [taskEntity, setTaskEntity] = useState();
    const { drop, clearDrop, findEvent, setItemLoading } = useKanbanBoardContext();
    const handleCancel = useCallback((undoMove = false) => {
        clearDrop(undoMove);
        setTask(undefined);
        setTaskEntity(undefined);
    }, [clearDrop]);
    const setLoading = useCallback((loading) => {
        drop && setItemLoading(drop.item, loading);
    }, [drop, setItemLoading]);
    const findTaskAndTaskEntity = useCallback((taskId) => {
        setLoading(true);
        let didAbort = false;
        const { result, abort } = findTaskAndEntity(taskId);
        // handle without await to allow returning callback to abort
        result.then(value => {
            var _a;
            setLoading(false);
            // skip set state for aborted request
            if (didAbort) {
                return;
            }
            else if (value.task && value.taskEntity) {
                setTask(value.task);
                setTaskEntity(value.taskEntity);
                return;
            }
            handleCancel(true);
            (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, value.error || {
                statusCode: 500,
                error: 'Internal Server Error',
                message: 'An error occurred.'
            });
        });
        return () => {
            didAbort = true;
            abort();
            setLoading(false);
        };
    }, [handleCancel, setLoading]);
    useEffect(() => {
        if (drop) {
            // when drop is set, fetch task and taskEntity
            // and return callback to abort requests
            return findTaskAndTaskEntity(drop.item.taskId);
        }
    }, [drop, findTaskAndTaskEntity]);
    if (!task || !drop || !drop.dropColumn.state) {
        return null;
    }
    return (React.createElement(TaskBoardStatusAction, { task: task, taskEntity: taskEntity, target: drop.dropColumn.state.name || '', findEventName: findEvent, setIsLoading: (taskId, loading) => {
            if (drop.item.taskId === taskId) {
                setLoading(loading);
            }
        }, onCancel: () => handleCancel(true), onError: error => {
            onError === null || onError === void 0 ? void 0 : onError(error);
            handleCancel(true);
        }, onChange: task => {
            handleCancel();
            onChange === null || onChange === void 0 ? void 0 : onChange(task);
        } }));
};
const itemFieldOptions = [
    { key: 'id', label: 'ID Field', helperText: 'Field for Card ID' },
    { key: 'title', label: 'Title Field', helperText: 'Field for Card Title' },
    {
        key: 'description',
        label: 'Description Field',
        helperText: 'Field for Card Description'
    },
    {
        key: 'taskId',
        label: 'Task ID Field',
        helperText: 'Field for Card Task ID'
    },
    {
        key: 'status',
        label: 'Status Field',
        helperText: 'Field for Card Status Label'
    },
    {
        key: 'workflowId',
        label: 'Workflow ID Field',
        helperText: 'Field for Card Status Color'
    },
    {
        key: 'firstName',
        label: 'Assignee First Name Field',
        helperText: 'Field for Assignee First Name'
    },
    {
        key: 'lastName',
        label: 'Assignee Last Name Field',
        helperText: 'Field for Assignee Last Name'
    },
    {
        key: 'deleted',
        label: 'Deleted Field',
        helperText: 'Field for Card Deleted styling'
    }
];
const StyledDiv = styled.div `
  .MuiAccordion-root,
  .MuiAccordion-root.Mui-expanded {
    margin: 0 -12px;
  }
  .MuiAccordionSummary-root {
    max-height: 50px;
    padding-left: 12px;
    padding-right: 12px;
    &.Mui-expanded {
      min-height: unset;
    }
  }
  .MuiAccordionDetails-root {
    padding-left: 12px;
    padding-right: 12px;
    padding-bottom: 12px;
  }
  .config-field-control {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .delete-button {
    margin-right: 8px;
  }
  .column-title {
    display: flex;
    align-items: center;
    width: 100%;
  }
  .details {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
`;
export const SiteKanbanBoardConfigurator = props => {
    const { component, update, insert, remove } = useConfigComponent(props.component);
    const { queries } = useQueryContext();
    const [expanded, setExpanded] = useState();
    const cProps = component.props || {};
    const { workflow } = useWorkflowItem({ workflowId: cProps.workflowId }) || {};
    const columns = cProps.columns;
    const workflowPaths = useMemo(() => {
        if (!workflow) {
            return [];
        }
        const rootNode = `/${workflow.stateNode.name}`;
        return workflow.getAllPaths().filter(path => path !== rootNode);
    }, [workflow]);
    const getQueryOptions = (queryId) => {
        return queries.filter(query => query.id === queryId);
    };
    return (React.createElement(StyledDiv, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(WorkflowPicker, { value: cProps.workflowId, textFieldProps: { size: 'small' }, onChange: workflow => update('props.workflowId', workflow === null || workflow === void 0 ? void 0 : workflow.id) })),
        React.createElement(Box, { mb: 1 },
            React.createElement(Divider, null)),
        React.createElement(Button, { fullWidth: true, size: 'small', variant: 'outlined', startIcon: React.createElement(Add, null), onClick: () => {
                if (!columns) {
                    update('props.columns', []);
                }
                insert('props.columns', {
                    id: uuid(),
                    name: `Column ${((columns === null || columns === void 0 ? void 0 : columns.length) || 0) + 1}`
                });
            } }, "Add Column"),
        React.createElement(Box, { my: 1 }, !columns || columns.length === 0 ? (React.createElement(Typography, { color: 'textSecondary' }, "No Columns configured.")) : (React.createElement(Typography, { variant: 'overline', color: 'textSecondary' }, "Columns"))), columns === null || columns === void 0 ? void 0 :
        columns.map((column, index) => {
            var _a, _b, _c, _d, _e, _f;
            const columnPath = `props.columns[${index}]`;
            const queryOptions = getQueryOptions(column.queryId);
            return (React.createElement(Accordion, { key: column.id, expanded: column.id === expanded, onChange: (_, expanded) => setExpanded(expanded ? column.id : undefined) },
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
                    React.createElement(IconButton, { size: 'small', className: 'delete-button', onClick: event => {
                            event.stopPropagation();
                            remove(columnPath);
                        } },
                        React.createElement(RemoveCircleOutlineOutlined, { fontSize: 'small' })),
                    React.createElement(Typography, { align: 'center', variant: 'body2', className: 'column-title' }, column.name)),
                React.createElement(AccordionDetails, null,
                    React.createElement("div", { className: 'details' },
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(ConfigTextField, { fullWidth: true, label: 'Name', value: column.name || '', onChange: event => {
                                    update(`${columnPath}.name`, event.target.value);
                                } })),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryPicker, { label: 'Query', value: column.queryId ? [column.queryId] : [], onChange: queries => {
                                    var _a;
                                    const queryId = (_a = queries.pop()) === null || _a === void 0 ? void 0 : _a.id;
                                    update(`${columnPath}.queryId`, queryId);
                                } })),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(Autocomplete, { fullWidth: true, disabled: !workflow, options: workflowPaths, value: column.workflowPath || null, getOptionLabel: option => parseTaskStatus(option), onChange: (_, value) => {
                                    update(`${columnPath}.workflowPath`, value || undefined);
                                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Workflow State', placeholder: 'Select Workflow State', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(FormControlLabel, { label: 'Filter by Status', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !!column.filterByStatus, onChange: () => {
                                        update(`${columnPath}.filterByStatus`, !column.filterByStatus);
                                    } }) }),
                            React.createElement(FormHelperText, null,
                                "Enable filtering the ",
                                React.createElement("strong", null, "Query"),
                                " based on the",
                                ' ',
                                React.createElement("strong", null, "Workflow State"),
                                " using the",
                                React.createElement("strong", null, "Status Field"),
                                ".")),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Search Filter Columns")),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { multiple: true, hideQueryName: true, options: queryOptions, queryId: column.queryId, queryField: column.searchFields || [], onChange: value => {
                                    const fields = value.map(v => v.queryField);
                                    update(`${columnPath}.searchFields`, fields);
                                } })),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Card Fields")),
                        itemFieldOptions.map(field => {
                            var _a;
                            return (React.createElement(ConfigFieldControl, { key: field.key },
                                React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: field.label, queryId: column.queryId, queryField: (_a = column.itemFields) === null || _a === void 0 ? void 0 : _a[field.key], onChange: value => {
                                        update(`${columnPath}.itemFields.${field.key}`, value.queryField);
                                    } }),
                                React.createElement(FormHelperText, null, field.helperText)));
                        }),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Button"),
                            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                                "The selected ",
                                React.createElement("strong", null, "route"),
                                " will be navigated when clicking card button")),
                        React.createElement(SiteRouteAndParamPicker, { component: component, routeId: (_a = column.routeNavigation) === null || _a === void 0 ? void 0 : _a.routeId, routeParam: (_b = column.routeNavigation) === null || _b === void 0 ? void 0 : _b.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                                updateRouteAndParam(value, `${columnPath}.routeNavigation.routeId`, `${columnPath}.routeNavigation.routeParam`).forEach(([path, value]) => update(path, value));
                            } }),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: 'Select a Field', queryId: column.queryId, queryField: (_c = column.routeNavigation) === null || _c === void 0 ? void 0 : _c.field, onChange: value => {
                                    update(`${columnPath}.routeNavigation.field`, value.queryField);
                                } })),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Filter"),
                            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                                "Filter items by matching the selected",
                                ' ',
                                React.createElement("strong", null, "route param"),
                                " and",
                                ' ',
                                React.createElement("strong", null, "query field"),
                                ".")),
                        React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: (_d = column.filter) === null || _d === void 0 ? void 0 : _d.routeId, routeParam: (_e = column.filter) === null || _e === void 0 ? void 0 : _e.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                                updateRouteAndParam(value, `${columnPath}.filter.routeId`, `${columnPath}.filter.routeParam`).forEach(([path, value]) => update(path, value));
                            } }),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: 'Select a Field', queryId: column.queryId, queryField: (_f = column.filter) === null || _f === void 0 ? void 0 : _f.field, onChange: value => {
                                    update(`${columnPath}.filter.field`, value.queryField);
                                } }))))));
        })));
};
export const SiteKanbanBoardPlugin = {
    name: 'Site Kanban Board',
    type: 'site-kanban-board',
    component: SiteKanbanBoard,
    configurator: SiteKanbanBoardConfigurator
};
//# sourceMappingURL=SiteKanbanBoardPlugin.js.map